import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  fromEvent,
  merge,
  timer,
} from 'rxjs';
import {
  map,
  takeUntil,
  distinctUntilChanged,
  startWith,
} from 'rxjs/operators';
import {
  HEARTBEAT_TIMEOUT_MS,
  HEARTBEAT_URL,
  INTERMITTENT_LATENCY_MS,
  NETWORK_HEARTBEAT,
  NETWORK_QUALITY,
  NETWORK_STATUS,
  NetworkQuality,
} from '../../constants/constants';

@Injectable({
  providedIn: 'root',
})
export class NetworkService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private _isOnline$ = new BehaviorSubject<boolean>(navigator.onLine);
  private _networkQuality$ = new BehaviorSubject<NetworkQuality>(
    navigator.onLine ? NETWORK_QUALITY.GOOD : NETWORK_QUALITY.INTERMITTENT,
  );
  private _wasOffline = false;
  private heartbeatFailureCount: number =
    NETWORK_HEARTBEAT.RESET_FAILURE_COUNT;

  public isOnline$: Observable<boolean> = this._isOnline$
    .asObservable()
    .pipe(distinctUntilChanged());

  public isOffline$: Observable<boolean> = this.isOnline$.pipe(
    map((online) => !online),
  );

  public networkQuality$: Observable<NetworkQuality> = this._networkQuality$
    .asObservable()
    .pipe(distinctUntilChanged());

  get isOnline(): boolean {
    return this._isOnline$.value;
  }

  get networkQuality(): NetworkQuality {
    return this._networkQuality$.value;
  }

  get wasOffline(): boolean {
    return this._wasOffline;
  }

  constructor() {
    this.initNetworkListeners();
  }

  private initNetworkListeners(): void {
    const online$ = fromEvent(window, NETWORK_STATUS.ONLINE).pipe(
      map(() => true),
    );
    const offline$ = fromEvent(window, NETWORK_STATUS.OFFLINE).pipe(
      map(() => false),
    );

    merge(online$, offline$)
      .pipe(startWith(navigator.onLine), takeUntil(this.destroy$))
      .subscribe((isOnline) => {
        if (!isOnline) {
          this._wasOffline = true;
          this.heartbeatFailureCount = NETWORK_HEARTBEAT.OFFLINE_FAILURE_COUNT;
          this._networkQuality$.next(NETWORK_QUALITY.INTERMITTENT);
        } else {
          this.heartbeatFailureCount = NETWORK_HEARTBEAT.RESET_FAILURE_COUNT;
          this._networkQuality$.next(NETWORK_QUALITY.INTERMITTENT);
        }
        this._isOnline$.next(isOnline);
      });

    timer(
      NETWORK_HEARTBEAT.RESET_FAILURE_COUNT,
      NETWORK_HEARTBEAT.CHECK_INTERVAL_MS,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkRealConnectivity();
      });
  }

  private async checkRealConnectivity(): Promise<void> {
    if (!navigator.onLine) {
      this._isOnline$.next(false);
      this._networkQuality$.next(NETWORK_QUALITY.INTERMITTENT);
      this._wasOffline = true;
      this.heartbeatFailureCount = NETWORK_HEARTBEAT.OFFLINE_FAILURE_COUNT;
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        HEARTBEAT_TIMEOUT_MS,
      );
      const startedAt = performance.now();

      const response = await fetch(HEARTBEAT_URL, {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      if (!response.ok) {
        throw new Error(`Heartbeat failed with status ${response.status}`);
      }

      const elapsedMs = performance.now() - startedAt;
      this.heartbeatFailureCount = NETWORK_HEARTBEAT.RESET_FAILURE_COUNT;
      this._isOnline$.next(true);
      this._networkQuality$.next(
        elapsedMs >= INTERMITTENT_LATENCY_MS
          ? NETWORK_QUALITY.INTERMITTENT
          : NETWORK_QUALITY.GOOD,
      );
    } catch {
      this.heartbeatFailureCount += NETWORK_HEARTBEAT.FAILURE_INCREMENT;

      if (
        this.heartbeatFailureCount === NETWORK_HEARTBEAT.FIRST_FAILURE_COUNT &&
        navigator.onLine
      ) {
        this._isOnline$.next(true);
        this._networkQuality$.next(NETWORK_QUALITY.INTERMITTENT);
        return;
      }

      this._isOnline$.next(false);
      this._networkQuality$.next(NETWORK_QUALITY.INTERMITTENT);
      this._wasOffline = true;
    }
  }

  public forceCheck(): void {
    this.checkRealConnectivity();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
